import type { RwaAssetAvailabilityFilter } from "@repo/ports/v2";
import {
  createTransaction,
  safeRandomUUID,
  type PendingMutation,
} from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import {
  DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  queryKeys,
  withSessionQueryScope,
} from "~/lib/query-keys";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import {
  normalizeAssetUniverseGroups,
  normalizeAssetUniverseSummaries,
  normalizeAssetUniverseSymbols,
  type AssetUniverseCanonicalGroup,
  type AssetUniverseCanonicalSummaryLike,
  type NormalizedAssetUniverseGraph,
} from "../asset-universe-contract";
import type {
  AssetTokenizedEquityValuationModel,
  AssetUniverseCanonicalModel,
  AssetUniverseGraphHydrationModel,
  AssetUniverseSymbolLinkModel,
} from "./models";
import {
  normalizeAssetDomainAssetIds,
  toAssetTokenizedEquityValuationId,
  toAssetUniverseGraphHydrationId,
} from "./models";
import {
  applyPendingCanonicalEquityQuotes,
  applyPendingCanonicalMarketQuotes,
  mergeAssetUniverseCanonicalSeedRow,
} from "./canonical-market-quotes";
import type { BrowserAssetDomainCollectionOptions } from "./collection-types";
import { chunkArray } from "./collection-query-support";
import {
  getAssetDomainCollections,
  type AssetDomainCollections,
} from "./collection-registry";
import { syncCollectionRows } from "./collection-sync";
import { getAssetDomainDependencies } from "./dependencies";

const ASSET_UNIVERSE_STALE_TIME_MS = 300_000;
/** `assetTaxonomy.resolveSymbols` rejects requests above this many symbols. */
const ASSET_UNIVERSE_RESOLVE_SYMBOLS_BATCH_LIMIT = 50;

export function getAssetUniverseBySymbolsQueryOptions(input: {
  availabilityFilter?: RwaAssetAvailabilityFilter;
  assetClass?: string | null;
  assetIds?: readonly string[];
  platformApi?: PlatformApiTrpcClient;
  queryClient?: QueryClient;
  sessionScopeToken?: string;
  symbols: readonly string[];
}) {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(input.assetIds ?? []);
  const normalizedSymbols = normalizeAssetUniverseSymbols(input.symbols);
  const dependencies = getAssetDomainDependencies({
    platformApi: input.platformApi,
    queryClient: input.queryClient,
    sessionScopeToken: input.sessionScopeToken,
  });
  const sessionScope = {
    scopeToken: dependencies.sessionScopeToken,
  };
  const symbolsKey = normalizedSymbols.join("|");
  const assetIdsKey = normalizedAssetIds.join("|");
  const assetClassKey =
    normalizeAssetUniverseAssetClassFilter(input.assetClass) ?? "all";
  const availabilityFilter = input.availabilityFilter ?? "indexed";

  return {
    enabled: normalizedSymbols.length > 0,
    queryKey: withSessionQueryScope(
      queryKeys.assetDomain.universeSymbolsByKey(
        `${assetClassKey}::${availabilityFilter}::${symbolsKey}::${assetIdsKey}`,
      ),
      sessionScope,
    ),
    queryFn: async () =>
      await ensureAssetUniverseBySymbols(normalizedSymbols, {
        assetClass: input.assetClass,
        assetIds: normalizedAssetIds,
        availabilityFilter,
        platformApi: dependencies.platformApi,
        queryClient: dependencies.queryClient,
        sessionScopeToken: dependencies.sessionScopeToken,
      }),
    staleTime: ASSET_UNIVERSE_STALE_TIME_MS,
  } as const;
}

export async function ensureAssetUniverseBySymbols(
  symbols: readonly string[],
  options?: {
    availabilityFilter?: RwaAssetAvailabilityFilter;
    assetClass?: string | null;
    assetIds?: readonly string[];
    platformApi?: PlatformApiTrpcClient;
    queryClient?: QueryClient;
    sessionScopeToken?: string;
  },
): Promise<string[]> {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(
    options?.assetIds ?? [],
  );
  const normalizedSymbols = normalizeAssetUniverseSymbols(symbols);
  if (normalizedSymbols.length === 0) {
    return [];
  }

  const dependencies = getAssetDomainDependencies({
    platformApi: options?.platformApi,
    queryClient: options?.queryClient,
    sessionScopeToken: options?.sessionScopeToken,
  });
  const collections = getAssetDomainCollections({
    sessionScopeToken: dependencies.sessionScopeToken,
  });
  const assetClassFilter = normalizeAssetUniverseAssetClassFilter(
    options?.assetClass,
  );
  const availabilityFilter = options?.availabilityFilter ?? "indexed";
  const missingSymbolSet = new Set(
    normalizedSymbols.filter(
      (symbol) =>
        !hasCompleteAssetUniverseGraphForSymbol(
          collections,
          symbol,
          assetClassFilter,
          availabilityFilter,
        ),
    ),
  );
  const hasMissingRequestedRoute = normalizedAssetIds.some(
    (assetId) => !hasAssetUniverseRouteForAssetId(collections, assetId),
  );

  if (hasMissingRequestedRoute) {
    for (const symbol of normalizedSymbols) {
      missingSymbolSet.add(symbol);
    }
  }

  const missingSymbols = [...missingSymbolSet].sort((left, right) =>
    left.localeCompare(right),
  );

  if (missingSymbols.length === 0) {
    return normalizedSymbols;
  }

  const responses = await Promise.all(
    chunkArray(missingSymbols, ASSET_UNIVERSE_RESOLVE_SYMBOLS_BATCH_LIMIT).map(
      (chunk) =>
        dependencies.platformApi.assetTaxonomy.resolveSymbols.query({
          assetClass: options?.assetClass ?? undefined,
          availabilityFilter,
          symbols: chunk,
        }),
    ),
  );

  await seedAssetUniverseFromCanonicalGroups(
    responses.flatMap((response) =>
      response.results.flatMap((result) =>
        result.selected ? [result.selected] : [],
      ),
    ),
    {
      collections,
      sessionScopeToken: dependencies.sessionScopeToken,
    },
  );

  return normalizedSymbols;
}

export function normalizeAssetUniverseAssetClassFilter(
  assetClass: string | null | undefined,
): string | null {
  const normalized = assetClass?.trim().toLowerCase();
  return normalized && normalized.length > 0 ? normalized : null;
}

export function matchesAssetUniverseAssetClassFilter(input: {
  assetClass: string | null | undefined;
  filter: string | null;
}): boolean {
  if (!input.filter) {
    return true;
  }

  const assetClass = normalizeAssetUniverseAssetClassFilter(input.assetClass);
  if (!assetClass) {
    return false;
  }

  if (input.filter === "tradfi") {
    return assetClass === "equity" || assetClass === "fund";
  }

  return assetClass === input.filter;
}

export function readCanonicalForSymbolLink(
  collections: AssetDomainCollections,
  link: AssetUniverseSymbolLinkModel,
): AssetUniverseCanonicalModel | null {
  for (const canonical of collections.assetUniverseCanonical.state.values()) {
    if (canonical.assetCanonicalId === link.assetCanonicalId) {
      return canonical;
    }
  }

  return null;
}

function hasCompleteAssetUniverseGraphForSymbol(
  collections: AssetDomainCollections,
  symbol: string,
  assetClassFilter: string | null,
  availabilityFilter: RwaAssetAvailabilityFilter,
): boolean {
  for (const link of collections.assetUniverseSymbolLinks.state.values()) {
    if (link.symbol !== symbol) {
      continue;
    }

    const canonical = readCanonicalForSymbolLink(collections, link);
    if (
      matchesAssetUniverseAssetClassFilter({
        assetClass: link.assetClass ?? canonical?.assetClass,
        filter: assetClassFilter,
      }) &&
      matchesAssetUniverseAvailabilityFilter({
        canonical,
        filter: availabilityFilter,
      }) &&
      collections.assetUniverseGraphHydration.state.has(
        toAssetUniverseGraphHydrationId(link.assetCanonicalId),
      )
    ) {
      return true;
    }
  }

  return false;
}

export function matchesAssetUniverseAvailabilityFilter(input: {
  canonical: AssetUniverseCanonicalModel | null;
  filter: RwaAssetAvailabilityFilter;
}): boolean {
  if (input.filter === "glider_supported") {
    return input.canonical?.gliderSupported === true;
  }

  return true;
}

export async function seedAssetUniverseFromCanonicalGroups(
  groups: readonly AssetUniverseCanonicalGroup[],
  options?: BrowserAssetDomainCollectionOptions & {
    /**
     * Seed the caller's exact collection instances. ensureAssetUniverseBySymbols
     * passes the instances its completeness check ran against — the ones the
     * caller's live queries are bound to — so a cache eviction between check
     * and seed cannot divert the write to a fresh instance nobody observes.
     */
    collections?: AssetDomainCollections;
    /** Canonicals requested from getCanonicalGraphs, including missing rows. */
    requestedCanonicalIds?: readonly string[];
    /**
     * Present only for getCanonicalGraphs. `undefined` means valuation rows
     * were not requested and therefore must remain untouched; an empty array
     * is an authoritative empty replacement for the requested slice.
     */
    tokenizedEquityValuations?: readonly Omit<
      AssetTokenizedEquityValuationModel,
      "id" | "requestedCanonicalId"
    >[];
    /** Whether the valuation envelope is authoritative or degraded. */
    tokenizedEquityValuationsStatus?: "ok" | "unavailable";
    /** Controls whether absent valuation legs are authoritative deletions. */
    valuationAvailabilityFilter?: "indexed" | "glider_supported";
  },
): Promise<string[]> {
  const authoritativeValuations =
    options?.tokenizedEquityValuationsStatus === "ok" &&
    options.tokenizedEquityValuations !== undefined
      ? options.tokenizedEquityValuations
      : undefined;
  if (groups.length === 0 && authoritativeValuations === undefined) {
    return [];
  }

  const sessionScopeToken =
    options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const collections =
    options?.collections ??
    getAssetDomainCollections({
      sessionScopeToken,
    });
  const requestedCanonicalIds = options?.requestedCanonicalIds ?? [];
  const valuationAvailabilityFilter =
    options?.valuationAvailabilityFilter ?? "indexed";
  const normalized = normalizeAssetUniverseGroups({
    groups,
    updatedAt: Date.now(),
  });
  await applyNormalizedAssetUniverseToCollections({
    collections,
    normalized,
  });
  if (authoritativeValuations !== undefined) {
    await replaceTokenizedEquityValuationSlice({
      collections,
      groups,
      requestedCanonicalIds,
      rows: authoritativeValuations,
      valuationAvailabilityFilter,
    });
    await markTokenizedEquityValuationHydration({
      collections,
      groups,
      requestedCanonicalIds,
      valuationAvailabilityFilter,
    });
  }
  await applyPendingCanonicalMarketQuotes({
    collections,
    sessionScopeToken,
  });
  await applyPendingCanonicalEquityQuotes({
    collections,
    sessionScopeToken,
  });

  return normalized.assetUniverseCanonical.map((row) => row.assetCanonicalId);
}

async function replaceTokenizedEquityValuationSlice(input: {
  collections: AssetDomainCollections;
  groups: readonly AssetUniverseCanonicalGroup[];
  requestedCanonicalIds: readonly string[];
  rows: readonly Omit<
    AssetTokenizedEquityValuationModel,
    "id" | "requestedCanonicalId"
  >[];
  valuationAvailabilityFilter: "indexed" | "glider_supported";
}): Promise<void> {
  const requestedCanonicalIds = new Set(
    [...input.requestedCanonicalIds]
      .map((canonicalId) => canonicalId.trim())
      .filter((canonicalId) => canonicalId.length > 0),
  );
  const groupCanonicalIds = new Set<string>();
  const provenanceByAssetId = new Map<string, string[]>();
  for (const group of input.groups) {
    const canonicalId = group.underlying.assetCanonicalId.trim();
    if (canonicalId.length > 0) {
      groupCanonicalIds.add(canonicalId);
      for (const assetId of [
        ...group.issuedProducts.flatMap((product) =>
          product.assets.map((asset) => asset.assetId),
        ),
        ...group.tokenRepresentations.map(
          (representation) => representation.assetId,
        ),
      ]) {
        const provenances = provenanceByAssetId.get(assetId) ?? [];
        if (!provenances.includes(canonicalId)) {
          provenances.push(canonicalId);
          provenances.sort((left, right) => left.localeCompare(right));
          provenanceByAssetId.set(assetId, provenances);
        }
      }
    }
  }
  const authoritativeProvenanceIds =
    requestedCanonicalIds.size > 0 ? requestedCanonicalIds : groupCanonicalIds;

  const rows = input.rows.map((row) => {
    const assetProvenances = provenanceByAssetId.get(row.assetId) ?? [];
    const requestedAssetProvenances = assetProvenances.filter((canonicalId) =>
      requestedCanonicalIds.has(canonicalId),
    );
    const requestedCanonicalId =
      (requestedAssetProvenances.includes(row.canonicalId)
        ? row.canonicalId
        : requestedAssetProvenances[0]) ??
      (requestedCanonicalIds.has(row.canonicalId)
        ? row.canonicalId
        : [...requestedCanonicalIds].sort((left, right) =>
            left.localeCompare(right),
          )[0]) ??
      assetProvenances[0] ??
      row.canonicalId;
    return {
      ...row,
      id: toAssetTokenizedEquityValuationId({
        assetId: row.assetId,
        canonicalId: row.canonicalId,
      }),
      requestedCanonicalId,
    } satisfies AssetTokenizedEquityValuationModel;
  });
  if (authoritativeProvenanceIds.size === 0) {
    return;
  }

  // Only requested/group canonical ids are authoritative. Conflict rows may
  // cite another canonical incidentally and must never widen deletion scope.
  // An `indexed` response deletes absent siblings; `glider_supported` is a
  // partial view and updates/inserts only rows it returned.
  const collection = input.collections.assetTokenizedEquityValuations;
  const currentRows = collection.state;
  const nextRowIds = new Set(rows.map((row) => row.id));
  const deleteKeys: string[] = [];
  if (input.valuationAvailabilityFilter === "indexed") {
    for (const existing of currentRows.values()) {
      if (
        authoritativeProvenanceIds.has(existing.requestedCanonicalId) &&
        !nextRowIds.has(existing.id)
      ) {
        deleteKeys.push(existing.id);
      }
    }
  }

  const existingRows = rows.filter((row) => currentRows.has(row.id));
  const insertedRows = rows.filter((row) => !currentRows.has(row.id));
  if (
    deleteKeys.length === 0 &&
    existingRows.length === 0 &&
    insertedRows.length === 0
  ) {
    return;
  }

  // One non-optimistic local transaction is accepted as a single sync
  // generation. Existing rows are delete+inserted so omitted optionals
  // disappear without exposing an empty intermediate slice.
  const transaction = createTransaction<Record<string, unknown>>({
    autoCommit: false,
    mutationFn: async ({ transaction: committedTransaction }) => {
      collection.utils.acceptMutations(committedTransaction);
    },
  });
  transaction.mutate(() => {
    const replacementDeleteKeys = [
      ...deleteKeys,
      ...existingRows.map((row) => row.id),
    ];
    if (replacementDeleteKeys.length > 0) {
      collection.delete(replacementDeleteKeys, { optimistic: false });
    }
    if (insertedRows.length > 0) {
      collection.insert(insertedRows, { optimistic: false });
    }
  });
  for (const row of existingRows) {
    const deletedMutation = transaction.mutations.find(
      (mutation) => mutation.key === row.id && mutation.type === "delete",
    );
    if (!deletedMutation) {
      throw new Error(`Missing valuation replacement delete for ${row.id}`);
    }
    const replacementRow: Record<string, unknown> = { ...row };
    const timestamp = new Date();
    transaction.mutations.push({
      changes: replacementRow,
      collection: deletedMutation.collection,
      createdAt: timestamp,
      globalKey: deletedMutation.globalKey,
      key: row.id,
      metadata: undefined,
      modified: replacementRow,
      mutationId: safeRandomUUID(),
      optimistic: false,
      original: {},
      syncMetadata: {},
      type: "insert",
      updatedAt: timestamp,
    } satisfies PendingMutation<Record<string, unknown>, "insert">);
  }
  await transaction.commit();
}

async function markTokenizedEquityValuationHydration(input: {
  collections: AssetDomainCollections;
  groups: readonly AssetUniverseCanonicalGroup[];
  requestedCanonicalIds: readonly string[];
  valuationAvailabilityFilter: "indexed" | "glider_supported";
}): Promise<void> {
  const canonicalIds = [
    ...new Set(
      [
        ...input.requestedCanonicalIds,
        ...input.groups.map((group) => group.underlying.assetCanonicalId),
      ]
        .map((canonicalId) => canonicalId.trim())
        .filter((canonicalId) => canonicalId.length > 0),
    ),
  ];
  const updatedAt = Date.now();
  const rows: AssetUniverseGraphHydrationModel[] = canonicalIds.map(
    (assetCanonicalId) => ({
      assetCanonicalId,
      graphRows: "complete",
      id: toAssetUniverseGraphHydrationId(assetCanonicalId),
      schemaVersion: 1,
      updatedAt,
      valuationAvailabilityFilter: input.valuationAvailabilityFilter,
      valuationRows: "complete",
      valuationUpdatedAt: updatedAt,
    }),
  );
  if (rows.length === 0) {
    return;
  }
  await syncCollectionRows(
    input.collections.assetUniverseGraphHydration,
    rows,
    rows.map((row) => row.id),
    { removeMissing: false },
  );
}

/**
 * Merge complete canonical summary rows without touching issuer products or
 * execution routes. A summary response means "graph not requested", never
 * "this canonical has no graph".
 */
export async function seedAssetUniverseFromCanonicalSummaries(
  summaries: readonly AssetUniverseCanonicalSummaryLike[],
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  if (summaries.length === 0) {
    return [];
  }

  const sessionScopeToken =
    options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const collections = getAssetDomainCollections({ sessionScopeToken });
  const normalized = normalizeAssetUniverseSummaries({
    summaries,
    updatedAt: Date.now(),
  });

  await Promise.all([
    syncCollectionRows(
      collections.assetUniverseCanonical,
      normalized.assetUniverseCanonical,
      normalized.assetUniverseCanonical.map((row) => row.id),
      {
        mergeExisting: mergeAssetUniverseCanonicalSeedRow,
        removeMissing: false,
      },
    ),
    syncCollectionRows(
      collections.assetUniverseSymbolLinks,
      normalized.assetUniverseSymbolLinks,
      normalized.assetUniverseSymbolLinks.map((row) => row.id),
      { removeMissing: false },
    ),
  ]);
  await applyPendingCanonicalMarketQuotes({
    collections,
    sessionScopeToken,
  });
  await applyPendingCanonicalEquityQuotes({
    collections,
    sessionScopeToken,
  });

  return normalized.assetUniverseCanonical.map((row) => row.assetCanonicalId);
}

function hasAssetUniverseRouteForAssetId(
  collections: AssetDomainCollections,
  assetId: string,
): boolean {
  for (const route of collections.assetUniverseRoutes.state.values()) {
    if (route.assetId === assetId) {
      return true;
    }
  }

  return false;
}
function applyNormalizedAssetUniverseToCollections(input: {
  collections: AssetDomainCollections;
  normalized: NormalizedAssetUniverseGraph;
}) {
  const canonicalRows = input.normalized.assetUniverseCanonical;
  const graphHydrationRows: AssetUniverseGraphHydrationModel[] =
    canonicalRows.map((row) => ({
      assetCanonicalId: row.assetCanonicalId,
      graphRows: "complete",
      id: toAssetUniverseGraphHydrationId(row.assetCanonicalId),
      schemaVersion: 1,
      updatedAt: row.updatedAt,
    }));

  return Promise.all([
    syncCollectionRows(
      input.collections.assetUniverseCanonical,
      canonicalRows,
      canonicalRows.map((row) => row.id),
      {
        mergeExisting: mergeAssetUniverseCanonicalSeedRow,
        removeMissing: false,
      },
    ),
    syncCollectionRows(
      input.collections.assetUniverseGraphHydration,
      graphHydrationRows,
      graphHydrationRows.map((row) => row.id),
      { removeMissing: false },
    ),
    syncCollectionRows(
      input.collections.assetUniverseProducts,
      input.normalized.assetUniverseProducts,
      input.normalized.assetUniverseProducts.map((row) => row.id),
      { removeMissing: false },
    ),
    syncCollectionRows(
      input.collections.assetUniverseRoutes,
      input.normalized.assetUniverseRoutes,
      input.normalized.assetUniverseRoutes.map((row) => row.id),
      { removeMissing: false },
    ),
    syncCollectionRows(
      input.collections.assetUniverseSymbolLinks,
      input.normalized.assetUniverseSymbolLinks,
      input.normalized.assetUniverseSymbolLinks.map((row) => row.id),
      { removeMissing: false },
    ),
  ]);
}
